import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatLayers } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { usePrintPage } from "@/lib/usePrintPage";
import { Printer, ArrowLeft, RefreshCw } from "lucide-react";

/**
 * Order summary for the sales team → schuur.
 * Per-locatie sectioned: elke locatie krijgt zijn eigen blok zodat bonnen
 * per kas afgedrukt kunnen worden.
 */
export default function OrderSummary() {
    usePrintPage("a4");
    const { id } = useParams();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const initialLocFilter = searchParams.get("location_id") || "";
    const autoPrint = searchParams.get("auto_print") === "1";
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState(initialLocFilter); // location_id filter; "" = all
    const [locMap, setLocMap] = useState({});
    const [pendingPrintFilter, setPendingPrintFilter] = useState(null);
    const autoPrinted = useRef(false);
    const [loadError, setLoadError] = useState("");

    useEffect(() => {
        api.get("/locations")
            .then((r) => setLocMap(Object.fromEntries(r.data.map((l) => [l.id, l]))))
            .catch(() => {});
    }, []);

    const load = useCallback((loc) => {
        const q = loc ? `?location_id=${encodeURIComponent(loc)}` : "";
        setData(null);
        setLoadError("");
        api.get(`/orders/${id}/summary${q}`)
            .then((r) => setData(r.data))
            .catch((e) => setLoadError(formatApiErrorDetail(e.response?.data?.detail ?? "Samenvatting kon niet worden geladen")));
    }, [id]);
    useEffect(() => {
        load(filter);
    }, [filter, load]);

    // Auto-print when launched from Printcentrum (?auto_print=1).
    useEffect(() => {
        if (!autoPrint || !data || autoPrinted.current || data.totals?.is_stale) return;
        autoPrinted.current = true;
        const t = setTimeout(() => window.print(), 400);
        return () => clearTimeout(t);
    }, [autoPrint, data]);

    useEffect(() => {
        if (!data || pendingPrintFilter === null || pendingPrintFilter !== filter) return;
        setPendingPrintFilter(null);
        const t = setTimeout(() => window.print(), 0);
        return () => clearTimeout(t);
    }, [data, filter, pendingPrintFilter]);

    if (loadError) {
        return <div className="p-12 text-red-800" role="alert">{loadError} <button type="button" onClick={() => load(filter)} className="font-semibold underline">Opnieuw proberen</button></div>;
    }
    if (!data) return <div className="p-12 text-ink-500">Laden…</div>;

    const groups = data.by_location || [];
    const locations = Array.from(
        new Map(groups.map((g) => [g.location_id, { id: g.location_id, code: g.location_code, name: g.location_name }])).values()
    );

    return (
        <div>
            <div className="p-8 lg:p-12 max-w-4xl no-print mx-auto">
                <button
                    onClick={() => nav(`/bestellingen/${id}`)}
                    className="text-sm text-ink-500 hover:text-florefy-900 mb-4 flex items-center gap-1"
                    data-testid="btn-back-to-order"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Terug naar bestelling
                </button>
                <PageHeader
                    kicker={`Samenvatting voor ${data.customer?.name || ""}`}
                    title={`${data.totals.total_products} planten · ${data.totals.total_units} stuks`}
                    subtitle={`Leverdatum ${data.delivery_date} · ${data.totals.total_locations || groups.length} locatie${(data.totals.total_locations || groups.length) === 1 ? "" : "s"} — bonnen per locatie afdrukken`}
                    testid="summary-header"
                    actions={
                        <button
                            onClick={() => window.print()}
                            disabled={data.totals.is_stale}
                            title={data.totals.is_stale ? "Bestelling eerst opnieuw berekenen" : ""}
                            data-testid="btn-print-summary"
                            className="px-4 py-2 rounded-md bg-florefy-900 hover:bg-florefy-700 text-white text-sm flex items-center gap-2 disabled:cursor-not-allowed disabled:bg-ink-300"
                        >
                            <Printer className="h-4 w-4" /> Print {filter ? `locatie` : "alles"}
                        </button>
                    }
                />

                {/* Karren + platen totalen (komt uit laatste berekening) */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="summary-totals">
                    <StatTile
                        label="Totaal stuks"
                        value={data.totals.total_units}
                        testid="summary-totals-units"
                    />
                    <StatTile
                        label="Karren totaal"
                        value={data.totals.total_carts ?? "—"}
                        helper={data.totals.total_carts == null ? "Nog niet berekend" : null}
                        accent
                        testid="summary-totals-carts"
                    />
                    <StatTile
                        label="Platen totaal"
                        value={data.totals.total_plates ?? "—"}
                        helper={data.totals.total_plates == null ? "Nog niet berekend" : null}
                        accent
                        testid="summary-totals-plates"
                    />
                </div>

                {data.totals.is_stale && (
                    <div
                        className="bg-yellow-50 border border-yellow-400 text-yellow-900 text-sm p-3 mb-4 rounded-sm flex gap-2"
                        data-testid="summary-stale"
                    >
                        <RefreshCw className="h-4 w-4 shrink-0 mt-0.5 text-yellow-700" />
                        <div>
                            <span className="font-semibold">Bestelling bewerkt na de berekening</span>{" "}
                            — karren/platen-totalen zijn van een oudere versie. Druk op &quot;Opnieuw berekenen&quot; in de bestelling voor up-to-date aantallen.
                        </div>
                    </div>
                )}

                <OptionsCard data={data} />

                {/* Location filter pills */}
                <div className="flex flex-wrap gap-2 mb-6" data-testid="summary-location-filter">
                    <button
                        onClick={() => setFilter("")}
                        className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
                            !filter
                                ? "bg-florefy-900 text-white border-florefy-900"
                                : "bg-white text-ink-700 border-border hover:bg-ink-50"
                        }`}
                        data-testid="summary-filter-all"
                    >
                        Alle locaties
                    </button>
                    {locations.map((loc) => (
                        <button
                            key={loc.id}
                            onClick={() => setFilter(loc.id)}
                            className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
                                filter === loc.id
                                    ? "bg-florefy-900 text-white border-florefy-900"
                                    : "bg-white text-ink-700 border-border hover:bg-ink-50"
                            }`}
                            data-testid={`summary-filter-${loc.code}`}
                        >
                            {loc.code} · {loc.name}
                        </button>
                    ))}
                </div>

                {groups.map((g) => (
                    <LocationBlock
                        key={g.location_id || "none"}
                        group={g}
                        color={locMap[g.location_id]?.color || "#94a3b8"}
                        onPrintLocation={() => {
                            if (data.totals.is_stale) return;
                            setData(null);
                            setFilter(g.location_id);
                            setPendingPrintFilter(g.location_id);
                        }}
                    />
                ))}
                {groups.length === 0 && (
                    <div className="text-sm text-ink-500 py-8 text-center" data-testid="summary-empty">
                        Geen regels in deze selectie.
                    </div>
                )}
            </div>

            <div className="print-only">
                {groups.map((g) => (
                    <div key={g.location_id || "none"} className="print-page p-8 bg-white text-black">
                        <div className="border-b-2 border-black pb-3 mb-5">
                            <div className="font-mono text-[10px] uppercase tracking-widest">
                                Florefy Flow · Samenvatting voor de schuur
                            </div>
                            <div className="text-2xl font-bold mt-1">{data.customer?.name}</div>
                            <div className="text-sm">{data.customer?.address}</div>
                            <div className="text-sm font-mono mt-1">
                                Leverdatum: {data.delivery_date} · {g.total_quantity} stuks
                            </div>
                            {(data.totals.total_carts != null || data.totals.total_plates != null) && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {data.totals.total_carts != null && (
                                        <div className="inline-block px-3 py-1 border-2 border-black font-mono text-sm font-bold">
                                            {data.totals.total_carts} {data.totals.total_carts === 1 ? "kar" : "karren"}
                                        </div>
                                    )}
                                    {data.totals.total_plates != null && (
                                        <div className="inline-block px-3 py-1 border-2 border-black font-mono text-sm font-bold">
                                            {data.totals.total_plates} {data.totals.total_plates === 1 ? "plaat" : "platen"}
                                        </div>
                                    )}
                                    {data.totals.is_stale && (
                                        <div className="inline-block px-3 py-1 border-2 border-dashed border-black font-mono text-xs">
                                            ⚠ bestelling bewerkt — opnieuw berekenen
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="mt-2 inline-block px-2 py-1 border-2 border-black font-mono text-sm font-bold">
                                LOCATIE {g.location_code} · {g.location_name}
                            </div>
                        </div>
                        <PrintOptions data={data} />
                        <SummaryList items={g.items} print />
                        {data.notes && (
                            <div className="mt-5 text-xs">
                                <span className="font-semibold">Notities:</span> {data.notes}
                            </div>
                        )}
                        <div className="mt-4 text-[10px] font-mono">
                            Florefy Flow — Gegenereerd op {new Date().toLocaleString("nl-NL")}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function LocationBlock({ group, color, onPrintLocation }) {
    return (
        <div
            className="mb-8 bg-white border border-border rounded-md overflow-hidden shadow-sm"
            style={{ borderLeft: `6px solid ${color}` }}
            data-testid={`summary-location-block-${group.location_code}`}
        >
            <div
                className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border"
                style={{ backgroundColor: color + "1f" }}
            >
                <div className="flex items-center gap-3">
                    <div
                        className="h-9 w-9 rounded-sm border border-black/20 flex items-center justify-center font-mono font-bold text-ink-950 text-base shadow-sm"
                        style={{ backgroundColor: color }}
                    >
                        {group.location_code}
                    </div>
                    <div>
                        <div className="text-base font-semibold text-ink-900 leading-tight">
                            {group.location_name}
                        </div>
                        <div className="font-mono text-[11px] uppercase tracking-widest text-ink-500 mt-0.5">
                            {group.items.length} product{group.items.length === 1 ? "" : "en"} · {group.total_quantity} stuks
                        </div>
                    </div>
                </div>
                <button
                    onClick={onPrintLocation}
                    className="px-3 py-1.5 rounded-sm border border-border bg-white hover:bg-ink-50 text-xs font-medium flex items-center gap-2"
                    data-testid={`btn-print-summary-location-${group.location_code}`}
                    title={`Print alleen ${group.location_code}`}
                >
                    <Printer className="h-3.5 w-3.5" /> Print {group.location_code}
                </button>
            </div>
            <SummaryList items={group.items} embedded />
        </div>
    );
}

function SummaryList({ items, print = false, embedded = false }) {
    const wrapperCls = print
        ? "divide-y-2 divide-black border-2 border-black"
        : embedded
        ? "divide-y divide-border"
        : "bg-white border border-border rounded-md divide-y divide-border";
    return (
        <div className={wrapperCls} data-testid="summary-list">
            {items.map((it) => (
                <div
                    key={`${it.product_id}-${it.location_id}`}
                    className={`px-5 py-4 ${print ? "" : "hover:bg-ink-50"}`}
                >
                    <div className="flex items-baseline justify-between gap-4">
                        <div className="min-w-0">
                            <div className={`${print ? "text-base" : "text-base"} font-semibold text-ink-900`}>
                                {it.name}
                                {it.cultivar && (
                                    <span className="italic font-normal text-ink-700 ml-1">{it.cultivar}</span>
                                )}
                                <span className="font-mono text-sm font-normal text-ink-500 ml-2">
                                    {it.pot_size}
                                    {it.pot_liters ? ` · ${it.pot_liters}` : ""}
                                </span>
                            </div>
                            <div className={`mt-1 ${print ? "text-sm" : "text-sm"} text-ink-900 font-mono`}>
                                {formatLayers(it.quantity, it.units_per_layer)}
                            </div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="font-mono font-bold text-xl text-ink-900">{it.quantity}</div>
                            <div className="data-label">stuks</div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

const LABEL_TYPE_LABELS = {
    own: "Ons eigen etiket",
    customer: "Klant eigen etiket",
    none: "Geen etiket",
};

function OptionsCard({ data }) {
    return (
        <div
            className="mb-6 bg-white border border-border rounded-md p-5"
            data-testid="summary-options-card"
        >
            <div className="data-label mb-3 text-ink-700 font-semibold">Order-instellingen</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 text-sm">
                <Opt label="Type kar" value={data.cart_type || "CC"} testid="opt-cart-type" />
                <Opt label="Kwaliteit" value={data.quality || "A"} testid="opt-quality" />
                <Opt
                    label="Etiket"
                    value={LABEL_TYPE_LABELS[data.label_type] || data.label_type || "—"}
                    testid="opt-label-type"
                />
                <Opt
                    label="Stickers"
                    value={
                        data.stickers
                            ? `Ja${data.stickers_description ? ` · ${data.stickers_description}` : ""}`
                            : "Nee"
                    }
                    testid="opt-stickers"
                />
                <Opt
                    label="Opzetstukken"
                    value={data.risers ? "Ja (+7 posities)" : "Nee"}
                    testid="opt-risers"
                />
            </div>
        </div>
    );
}

function Opt({ label, value, testid }) {
    return (
        <div data-testid={testid}>
            <div className="data-label">{label}</div>
            <div className="mt-0.5 font-mono text-ink-900 text-sm break-words">
                {value}
            </div>
        </div>
    );
}

function PrintOptions({ data }) {
    return (
        <div className="mb-5 border border-black p-3 text-xs font-mono grid grid-cols-2 gap-x-4 gap-y-1">
            <div><strong>Type kar:</strong> {data.cart_type || "CC"}</div>
            <div><strong>Kwaliteit:</strong> {data.quality || "A"}</div>
            <div><strong>Etiket:</strong> {LABEL_TYPE_LABELS[data.label_type] || "—"}</div>
            <div>
                <strong>Stickers:</strong>{" "}
                {data.stickers ? `Ja${data.stickers_description ? ` (${data.stickers_description})` : ""}` : "Nee"}
            </div>
            <div className="col-span-2">
                <strong>Opzetstukken:</strong> {data.risers ? "Ja — berekend met 42 posities per paal" : "Nee — standaard 35 posities"}
            </div>
        </div>
    );
}



function StatTile({ label, value, helper, accent, testid }) {
    return (
        <div
            className={`border ${accent ? "border-florefy-200 bg-florefy-50" : "border-border bg-white"} rounded-sm p-3`}
            data-testid={testid}
        >
            <div className="data-label leading-tight">{label}</div>
            <div className={`font-mono font-bold leading-tight mt-1 ${accent ? "text-2xl text-florefy-900" : "text-xl text-ink-900"}`}>
                {value}
            </div>
            {helper && (
                <div className="text-[10px] font-mono uppercase text-ink-500 tracking-wider mt-1">
                    {helper}
                </div>
            )}
        </div>
    );
}
